import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InternalPageHeader } from "@/components/InternalPageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuickUpdatePage({ onLogout }) {
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [action, setAction] = useState("");
  const [quantity, setQuantity] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  // OCR state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/stock`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum 10MB allowed.");
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file.");
      return;
    }
    
    setOcrProcessing(true);
    setOcrResult(null);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/ocr/extract-stock`, formData, {
        timeout: 30000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success && response.data.extracted_data.length > 0) {
        setOcrResult(response.data);
        toast.success("Text extracted from image!");
        
        // Try to auto-match product
        const extracted = response.data.extracted_data[0];
        if (extracted.product_code) {
          const matchedProduct = products.find(p => 
            p.product_code.toUpperCase().includes(extracted.product_code) ||
            extracted.product_code.includes(p.product_code.split(' ')[0].toUpperCase())
          );
          
          if (matchedProduct) {
            setSelectedProduct(matchedProduct);
            toast.success(`Matched product: ${matchedProduct.product_code}`);
          }
        }
        
        // Auto-fill quantity if found
        if (extracted.quantity) {
          setQuantity(extracted.quantity.toString());
        }
      } else {
        toast.error("Could not extract stock info from image. Try a clearer photo.");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Failed to process image";
      toast.error(msg);
      console.error("[OCR Error]", msg);
    } finally {
      setOcrProcessing(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const handleProductSelect = (productCode) => {
    const product = products.find((p) => p.product_code === productCode);
    setSelectedProduct(product);
    setSelectedBatch(""); // Reset batch selection
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProduct || !action || !quantity) {
      toast.error("Please fill all required fields");
      return;
    }

    // Check if product has batches and batch is required
    if (selectedProduct.dye_lots && selectedProduct.dye_lots.length > 0 && !selectedBatch) {
      toast.error("Please select a batch number");
      return;
    }

    if (parseFloat(quantity) <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/stock/quick-update`, {
        product_code: selectedProduct.product_code,
        action: action,
        quantity: parseFloat(quantity),
        batch_number: selectedBatch,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        
        // Reset form
        setSelectedProduct(null);
        setAction("");
        setQuantity("");
        setSelectedBatch("");
        
        // Refresh products
        fetchProducts();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Update failed";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] select-none w-full overflow-x-hidden">
      <InternalPageHeader
        title="QUICK UPDATE"
        subtitle="Fast stock corrections for logistics and admin teams"
        onBack={() => navigate("/stock")}
        backTestId="quick-update-back-button"
        leading={<img src="/logo.jpeg" alt="Red Magma" className="w-8 h-8 object-contain rounded" />}
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-4 sm:p-8">
          <div className="mb-6">
            <h2 className="heading-font text-3xl mb-2 text-[#09090b]">Update Stock</h2>
            <p className="text-[#64748b]">
              Update stock for sales, reservations, or releases
            </p>
          </div>

          {/* Image Upload for OCR */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Scan from Photo</span>
              </div>
              {ocrResult && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOcrResult(null)}
                  className="text-blue-600 h-7"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-blue-700 mb-3">
              Take a photo of stock label or document to auto-fill product & quantity
            </p>
            <input
              ref={imageInputRef}
              id="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
              className="sr-only"
              data-testid="camera-input"
            />
            <input
              ref={galleryInputRef}
              id="gallery-input"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="sr-only"
              data-testid="gallery-input"
            />
            <div className="flex gap-2">
              <label htmlFor="camera-input" className="flex-1 cursor-pointer">
                <Button
                  type="button"
                  data-testid="take-photo-btn"
                  disabled={ocrProcessing}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full pointer-events-none"
                  asChild
                >
                  <span>{ocrProcessing ? "Processing..." : "Take Photo"}</span>
                </Button>
              </label>
              <label htmlFor="gallery-input" className="flex-1 cursor-pointer">
                <Button
                  type="button"
                  data-testid="upload-photo-btn"
                  disabled={ocrProcessing}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 w-full pointer-events-none"
                  asChild
                >
                  <span>Upload from Gallery</span>
                </Button>
              </label>
            </div>
            
            {ocrResult && (
              <div className="mt-3 p-2 bg-white rounded border text-xs">
                <p className="font-medium text-gray-700 mb-1">Extracted:</p>
                <p className="text-gray-600 whitespace-pre-wrap">{ocrResult.raw_text.slice(0, 200)}</p>
              </div>
            )}
          </div>

          {loadingProducts ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#DC2626] border-t-transparent"></div>
              <p className="mt-3 text-[#64748b]">Loading products...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Selection */}
              <div>
                <Label htmlFor="product" className="text-sm font-semibold text-[#09090b] mb-2 block">
                  Select Product <span className="text-[#DC2626]">*</span>
                </Label>
                <Select value={selectedProduct?.product_code || ""} onValueChange={handleProductSelect}>
                  <SelectTrigger
                    data-testid="product-select"
                    className="h-12 border-2 focus:ring-[#DC2626]"
                  >
                    <SelectValue placeholder="Choose a product..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.product_code}>
                        {product.product_code} - {product.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show current stock if product selected */}
              {selectedProduct && (
                <div className="bg-[#f8f9fa] p-4 rounded-lg border-l-4 border-[#DC2626]">
                  <p className="text-sm font-semibold text-[#09090b] mb-2">Current Stock:</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#64748b]">Available:</span>{" "}
                      <span className="number-font text-lg text-[#16a34a]">
                        {selectedProduct.available_qty}
                      </span>{" "}
                      {selectedProduct.unit}
                    </div>
                    <div>
                      <span className="text-[#64748b]">Reserved:</span>{" "}
                      <span className="number-font text-lg text-[#ca8a04]">
                        {selectedProduct.reserved_qty}
                      </span>{" "}
                      {selectedProduct.unit}
                    </div>
                  </div>
                  {selectedProduct.dye_lots && selectedProduct.dye_lots.length > 0 && (
                    <div className="mt-2">
                      <span className="text-[#64748b] text-sm">Batches:</span>{" "}
                      <span className="text-sm">{selectedProduct.dye_lots.join(", ")}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Selection */}
              <div>
                <Label htmlFor="action" className="text-sm font-semibold text-[#09090b] mb-2 block">
                  Action <span className="text-[#DC2626]">*</span>
                </Label>
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger
                    data-testid="action-select"
                    className="h-12 border-2 focus:ring-[#DC2626]"
                  >
                    <SelectValue placeholder="Select action..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sold">Sold (Reduce Available Stock)</SelectItem>
                    <SelectItem value="reserved">Reserved (Move to Reserved)</SelectItem>
                    <SelectItem value="released">Released (Return to Available)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity Input */}
              <div>
                <Label htmlFor="quantity" className="text-sm font-semibold text-[#09090b] mb-2 block">
                  Quantity (SQM) <span className="text-[#DC2626]">*</span>
                </Label>
                <Input
                  id="quantity"
                  data-testid="quantity-input"
                  type="number"
                  step="0.01"
                  placeholder="Enter quantity..."
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="h-12 text-base border-2 focus-visible:ring-[#DC2626]"
                />
              </div>

              {/* Batch Selection (REQUIRED) */}
              {selectedProduct && selectedProduct.dye_lots && selectedProduct.dye_lots.length > 0 && (
                <div>
                  <Label htmlFor="batch" className="text-sm font-semibold text-[#09090b] mb-2 block">
                    Batch Number <span className="text-[#DC2626]">*</span>
                  </Label>
                  <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                    <SelectTrigger
                      data-testid="batch-select"
                      className="h-12 border-2 focus:ring-[#DC2626]"
                    >
                      <SelectValue placeholder="Select batch..." />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProduct.dye_lots.map((batch, idx) => {
                        const [batchNum, batchQty] = batch.split(':');
                        return (
                          <SelectItem key={idx} value={batchNum}>
                            {batchNum} ({batchQty} {selectedProduct.unit} available)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#DC2626] mt-1 font-medium">
                    ⚠️ Required - quantity will be deducted from selected batch
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/stock")}
                  className="flex-1 h-12"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-update"
                  disabled={loading || !selectedProduct || !action || !quantity || (selectedProduct?.dye_lots?.length > 0 && !selectedBatch)}
                  className="flex-1 h-12 bg-[#DC2626] hover:bg-[#DC2626]/90 text-white font-semibold uppercase tracking-wide"
                >
                  {loading ? "UPDATING..." : "UPDATE STOCK"}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Suggestions Box */}
        <div className="mt-6 bg-[#fef2f2] border-l-4 border-[#DC2626] p-4 rounded-lg">
          <p className="text-sm font-semibold text-[#09090b] mb-2">💡 Suggestions:</p>
          <ul className="text-sm text-[#64748b] space-y-1">
            <li>• Use "Sold" when product is completely sold to a client</li>
            <li>• Use "Reserved" when stock is held for a pending order</li>
            <li>• Use "Released" when a reservation is cancelled</li>
            <li>• Batch number helps track which specific lot was used</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
